import { useEffect, useState } from 'react';

import { templatesApi } from '@/shared/api/transcriptor/templatesApi';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import type { DocumentTemplate, EncounterSchema } from '@/entities/templates';

import type { ParsedProposal } from '../lib';

export type CurrentState = {
  template?: DocumentTemplate;
  schema?: EncounterSchema;
  loading: boolean;
  failed: boolean;
};

/** Loads the current state of the object a proposal edits — the redline base. */
export const useCurrentProposalState = (
  parsed: ParsedProposal,
): CurrentState => {
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const baseID =
    parsed.kind === 'template' ? parsed.baseSeriesID : parsed.schemaID;
  // Start in the loading state when a base exists: initializing to false
  // painted a fabricated all-"Added" diff for one frame before the effect
  // engaged.
  const [state, setState] = useState<CurrentState>({
    loading: Boolean(baseID),
    failed: false,
  });

  useEffect(() => {
    if (!baseID) {
      setState({ loading: false, failed: false });
      return;
    }
    let active = true;
    setState({ loading: true, failed: false });
    const request =
      parsed.kind === 'template'
        ? templatesApi.getDocumentTemplateSeriesHead(baseID, organizationId)
        : templatesApi.getEncounterSchema(baseID, organizationId);
    request
      .then((current) => {
        if (!active) return;
        setState(
          parsed.kind === 'template'
            ? {
                template: current as DocumentTemplate,
                loading: false,
                failed: false,
              }
            : {
                schema: current as EncounterSchema,
                loading: false,
                failed: false,
              },
        );
      })
      .catch(() => {
        if (active) setState({ loading: false, failed: true });
      });
    return () => {
      active = false;
    };
  }, [baseID, parsed.kind, parsed, organizationId]);

  return state;
};
